/**
 * Freshness heartbeat — the single source of truth for how the product
 * talks about data liveness.
 *
 * Contract (mirrors the honesty framing in docs/PROJECT_STATE.md):
 *   - Fresh data is announced with confidence: "Live · updated 4 min ago".
 *   - Old data is stated calmly, never as an apology. "Quiet here — last
 *     verified 2 days ago" keeps trust; "⚠ Feed is 38 days old · check
 *     back later" kills it. Both are honest — only one sounds alive.
 *   - Absence is shown as absence ("No live signals in Sydney right now"),
 *     never faked and never dressed up.
 *   - An unparseable/missing timestamp is UNKNOWN, not "live". The old
 *     behaviour defaulted to "live" on a NaN timestamp, which is exactly
 *     the kind of small dishonesty this product cannot afford.
 *
 * Pure functions, injectable clock — unit-tested in freshness.test.ts.
 * Consumed by the feed pill (incident-bubbles.ts) and the area snapshot.
 */

export type FeedTone =
  | "live"        // < 36h old — the heartbeat
  | "stale"       // 36–72h — quiet, said calmly
  | "very_stale"  // ≥ 72h — still calm, still honest
  | "empty"       // feed loaded, zero signals
  | "unknown";    // timestamp missing/unparseable — never claim live

export interface HeartbeatInput {
  /** Feed-level generation timestamp (ISO 8601) from the API. */
  generatedAt?: string | null;
  /** Number of signals the surface is actually showing. */
  itemCount: number;
  /** Full pool size when the surface caps what it renders (e.g. the map
   *  shows the nearest 40 of 53). When it exceeds itemCount the copy says
   *  so — stating only the visible count would under-report, stating only
   *  the pool would overstate what's on screen. Optional: omitting it
   *  means "showing everything", the pre-existing behaviour. */
  totalCount?: number;
  /** "Sydney", "Melbourne", … falls back to "this city". */
  cityLabel?: string | null;
  /** Injectable clock for tests; defaults to Date.now(). */
  now?: number;
}

export interface Heartbeat {
  tone: FeedTone;
  /** One calm line, ready to render. */
  text: string;
}

/** Threshold boundaries, exported so tests and tooling agree with the UI. */
export const LIVE_MAX_HOURS = 36;
export const STALE_MAX_HOURS = 72;

/**
 * "just now" | "4 min ago" | "3h ago" | "2 days ago".
 * Future timestamps (clock skew between server and device) clamp to
 * "just now" — a feed from 30 seconds in the future is fresh, not broken.
 */
export function relativeAge(thenMs: number, nowMs: number): string {
  const ageMs = Math.max(0, nowMs - thenMs);
  const min = Math.floor(ageMs / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

export function signalCountLabel(n: number): string {
  return n === 1 ? "1 signal" : `${n} signals`;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * Honest label for a one-shot snapshot artefact — the AI intel files
 * (/api/intel-{city}.json) and the archived suburb-memory JSON. These are
 * not feeds with a heartbeat: they were generated once, so the honest
 * framing is the generation date stated plainly ("snapshot from 7 May
 * 2026"), never a relative age that implies a pulse. A missing or
 * unparseable timestamp is said as unknown — same rule as the heartbeat:
 * never silently fresh.
 *
 * UTC calendar date, deliberately: deterministic across devices, and at
 * day granularity the timezone offset is immaterial.
 */
export function snapshotLabel(generatedAt?: string | null): string {
  const ts = generatedAt ? Date.parse(generatedAt) : NaN;
  if (!Number.isFinite(ts)) return "snapshot date unverified";
  const d = new Date(ts);
  return `snapshot from ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export function feedHeartbeat(input: HeartbeatInput): Heartbeat {
  const now = input.now ?? Date.now();
  const city = input.cityLabel || "this city";
  const total = Math.max(input.totalCount ?? input.itemCount, input.itemCount);

  if (input.itemCount === 0) {
    // Signals can exist without being mappable (city-anchored items have
    // no real suburb pin). Saying "no live signals" then would be a lie —
    // say what's true: they exist, the map can't place them.
    if (total > 0) {
      return {
        tone: "empty",
        text: `${signalCountLabel(total)} nearby · none mapped to a suburb`,
      };
    }
    return { tone: "empty", text: `No live signals in ${city} right now` };
  }

  // One voice for the count: the full pool, plus "showing nearest N" when
  // the surface caps the visible subset.
  const countLabel =
    total > input.itemCount
      ? `${signalCountLabel(total)} · showing nearest ${input.itemCount}`
      : signalCountLabel(input.itemCount);

  const ts = input.generatedAt ? Date.parse(input.generatedAt) : NaN;
  if (!Number.isFinite(ts)) {
    // Honest about what we don't know — and never the green "live" dot.
    return {
      tone: "unknown",
      text: `${countLabel} · freshness unverified`,
    };
  }

  const ageHours = Math.max(0, now - ts) / 3_600_000;
  const ago = relativeAge(ts, now);

  if (ageHours < LIVE_MAX_HOURS) {
    return {
      tone: "live",
      text: `Live · updated ${ago} · ${countLabel}`,
    };
  }
  if (ageHours < STALE_MAX_HOURS) {
    return {
      tone: "stale",
      text: `Quiet lately — verified ${ago} · ${countLabel}`,
    };
  }
  return {
    tone: "very_stale",
    text: `Quiet here — last verified ${ago}`,
  };
}
